import { setMathMLDisplayAttribute } from '@/lib/utils';
import type { Editor } from '@tiptap/core';
import { Fragment, Node } from '@tiptap/pm/model';
import { TextSelection, type Transaction } from '@tiptap/pm/state';

/**
 * Returns the nearest scrollable ancestor of an element (one with overflow that creates a scroll container).
 * Used as the root for IntersectionObserver so "visible" means visible in the editor's scroll area.
 */
export function getScrollParent(element: Element): Element | null {
    let parent = element.parentElement;
    while (parent) {
        const { overflowY, overflow } = getComputedStyle(parent);
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay' || overflow === 'auto' || overflow === 'scroll') {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
}

/**
 * Regular expression to match LaTeX math strings wrapped in single dollar signs (not double).
 * This should not catch dollar signs which are not part of a math expression,
 * like those used for currency or other purposes.
 * It ensures that the dollar signs are not preceded or followed by digits or another $,
 * allowing for proper identification of inline math expressions.
 *
 * - `$x^2 + y^2 = z^2$` will match
 * - `This is $inline math$ in text.` will match
 * - `This is $100$ dollars.` will not match (as it is not a math expression)
 * - `This is $x^2 + y^2 = z^2$ and $100$ dollars.` will match both math expressions
 * - `$$x+y$$` will not match (double dollars are excluded)
 */
export const mathMigrationRegex = /(?<!\d|\$)\$(?!\$)(?:[^$\n]|\\$)*?(?<!\\)\$(?![\d$])/g;

/**
 * Creates a transaction that migrates existing math strings in the document to new math nodes.
 * This function traverses the document and replaces LaTeX math syntax (wrapped in single dollar signs)
 * with proper inline math nodes, preserving the mathematical content.
 *
 * @param editor - The editor instance containing the schema and configuration
 * @param tr - The transaction to modify with the migration operations
 * @returns The modified transaction with math string replacements
 *
 * @example
 * ```typescript
 * const editor = new Editor({ ... })
 * const tr = editor.state.tr
 * const updatedTr = createMathMigrateTransaction(editor, tr)
 * editor.view.dispatch(updatedTr)
 * ```
 */
export function createMathMigrateTransaction(editor: Editor, tr: Transaction, regex: RegExp = mathMigrationRegex) {
    // we traverse the document and replace all math nodes with the new math nodes
    tr.doc.descendants((node, pos) => {
        if (!node.isText || !node.text || !node.text.includes('$')) {
            return;
        }

        const { text } = node;

        const match = node.text.match(regex);
        if (!match) {
            return;
        }

        match.forEach(mathMatch => {
            const start = text.indexOf(mathMatch);
            const end = start + mathMatch.length;

            const from = tr.mapping.map(pos + start);

            const $from = tr.doc.resolve(from);
            const parent = $from.parent;
            const index = $from.index();

            const { inlineMath } = editor.schema.nodes;

            if (!parent.canReplaceWith(index, index + 1, inlineMath)) {
                return;
            }

            // Replace the math syntax with a new math node
            tr.replaceWith(tr.mapping.map(pos + start), tr.mapping.map(pos + end), inlineMath.create({ latex: mathMatch.slice(1, -1) }));
        });
    });

    // don't add to history
    tr.setMeta('addToHistory', false);
    return tr;
}

/**
 * Migrates existing math strings in the editor document to math nodes.
 * This function creates and dispatches a transaction that converts LaTeX math syntax
 * (text wrapped in single dollar signs) into proper inline math nodes. The migration
 * happens immediately and is not added to the editor's history.
 *
 * @param editor - The editor instance to perform the migration on
 *
 * @example
 * ```typescript
 * const editor = new Editor({
 *   extensions: [Mathematics],
 *   content: 'This is inline math: $x^2 + y^2 = z^2$ in text.'
 * })
 *
 * // Math strings will be automatically migrated to math nodes
 * migrateMathStrings(editor)
 * ```
 */
export function migrateMathStrings(editor: Editor, regex: RegExp = mathMigrationRegex) {
    const tr = createMathMigrateTransaction(editor, editor.state.tr, regex);
    editor.view.dispatch(tr);
}

/**
 * Regular expression to match LaTeX math strings wrapped in \( and \).
 * This matches inline math expressions in the form \(LaTeX content\).
 *
 * - `\(x^2 + y^2 = z^2\)` will match
 * - `Two matrices \(A=[a_{ij}]\) and \(B=[b_{ij}]\)` will match both expressions
 * - `\(A\pm B=[a_{ij}]\pm [b_{ij}]=[a_{ij} \pm b_{ij}]\)` will match
 */
export const zybooksInlineLatexRegex = /\\\((?:[^\\]|\\(?!\)))*?\\\)/g;

/**
 * Regular expression to match LaTeX display math strings wrapped in $$.
 * This matches block-level math expressions that can span multiple lines.
 *
 * - Matches content between $$ and $$
 * - Handles multiline expressions
 * - Allows for whitespace and line breaks
 * - Handles complex LaTeX environments like align, bmatrix, etc.
 */
export const zybooksBlockLatexRegex = /(\$\$[\s\S]*?\$\$)|(\\\[[\s\S]*?\\\])/g;

function fragmentWithoutEmptyZydefn(frag: Fragment): Fragment {
    const nodes: Node[] = [];
    frag.forEach(node => {
        if (node.type.name === 'zydefn' && node.content.size === 0) return;
        nodes.push(node);
    });
    return Fragment.fromArray(nodes);
}

function isBlockMath(text: string): boolean {
    return (text.startsWith('$$') && text.endsWith('$$')) || (text.startsWith('\\[') && text.endsWith('\\]'));
}

/**
 * Migrates existing zyBook math strings in the editor document to math nodes.
 * This function creates and dispatches a transaction that converts LaTeX math syntax
 * (text wrapped in \( and \) for inline math, or $$ for block math) into proper math nodes.
 * The migration happens immediately and is not added to the editor's history.
 *
 * @param editor - The editor instance to perform the migration on
 *
 * @example
 * ```typescript
 * const editor = new Editor({
 *   extensions: [Mathematics],
 *   content: 'This is inline math: \\(x^2 + y^2 = z^2\\) in text.'
 * })
 *
 * // Math strings will be automatically migrated to math nodes
 * migrateZybookMathStrings(editor)
 * ```
 */
export function migrateZybookMathStrings(editor: Editor) {
    const inlineTr = createZybookMathMigrateTransaction(editor, editor.state.tr);
    editor.view.dispatch(inlineTr);
    const blockTr = createBlockMathMigrateTransaction(editor, editor.state.tr);
    editor.view.dispatch(blockTr);
}

/**
 * Creates a transaction that migrates existing math strings in the document to new inline math nodes.
 * This function traverses the document and replaces LaTeX math syntax (wrapped in single dollar signs)
 * with proper inline math nodes, preserving the mathematical content.
 *
 * @param editor - The editor instance containing the schema and configuration
 * @param tr - The transaction to modify with the migration operations
 * @returns The modified transaction with math string replacements
 *
 * @example
 * ```typescript
 * const editor = new Editor({ ... })
 * const tr = editor.state.tr
 * const updatedTr = createMathMigrateTransaction(editor, tr)
 * editor.view.dispatch(updatedTr)
 * ```
 */
export function createZybookMathMigrateTransaction(editor: Editor, tr: Transaction) {
    const findMatchesWithIndex = (text: string, regex: RegExp) => {
        const matches = [];
        let match;
        while ((match = regex.exec(text)) !== null) {
            matches.push({
                match: match[0],
                index: match.index,
            });
        }
        return matches;
    };

    // we traverse the document and replace all math nodes with the new math nodes
    tr.doc.descendants((node, pos) => {
        if (!node.isText || !node.text) {
            return;
        }
        const { text } = node;

        // inline math node with \( and \)
        if (node.text.includes('\\(')) {
            const match = findMatchesWithIndex(node.text, zybooksInlineLatexRegex);
            if (match.length === 0) {
                return;
            }

            match.forEach(mathMatch => {
                const start = text.indexOf(mathMatch.match, mathMatch.index);
                const end = start + mathMatch.match.length;
                const from = tr.mapping.map(pos + start);
                const $from = tr.doc.resolve(from);
                const parent = $from.parent;
                const index = $from.index();
                const { inlineMath } = editor.schema.nodes;

                if (!parent.canReplaceWith(index, index + 1, inlineMath)) {
                    return;
                }

                tr.replaceWith(
                    tr.mapping.map(pos + start),
                    tr.mapping.map(pos + end),
                    inlineMath.create({ latex: mathMatch.match.slice(2, -2) }),
                );
            });
        }

        // inline math node with dollar signs
        if (text.match(mathMigrationRegex)) {
            const match = text.match(mathMigrationRegex);
            if (!match) {
                return;
            }
            let lastIndex = 0;
            match.forEach(mathMatch => {
                // Find each match after lastIndex to handle identical matches correctly
                const start = text.indexOf(mathMatch, lastIndex);
                const end = start + mathMatch.length;
                const from = tr.mapping.map(pos + start);
                const $from = tr.doc.resolve(from);
                const parent = $from.parent;
                const index = $from.index();
                const { inlineMath } = editor.schema.nodes;

                if (!parent.canReplaceWith(index, index + 1, inlineMath)) {
                    // Advance lastIndex anyway so we don't loop on the same match
                    lastIndex = end;
                    return;
                }

                tr.replaceWith(
                    tr.mapping.map(pos + start),
                    tr.mapping.map(pos + end),
                    inlineMath.create({ latex: mathMatch.slice(1, -1) }),
                );
                // Move lastIndex beyond this match so identical matches work correctly
                lastIndex = end;
            });
        }
    });

    // don't add to history
    tr.setMeta('addToHistory', false);
    return tr;
}

/**
 * Creates a transaction that migrates existing block math strings in the document to block math nodes.
 * This function traverses the document and replaces LaTeX math syntax (wrapped in $$ and $$)
 * with proper block math nodes, preserving the mathematical content.
 *
 * When $$ appears inside a paragraph that is a child of zydefnBlock, the paragraph cannot contain
 * blockMath (block nodes). We replace the paragraph with [blockMath, paragraph(rest)] so the
 * blockMath becomes a direct child of zydefnBlock.
 *
 * When $$ appears inside an inline zydefn, the zydefn cannot contain blockMath. We upgrade the
 * inline zydefn to zydefnBlock and replace the paragraph containing it with zydefnBlock(blockMath,
 * paragraph(rest)).
 *
 * @param editor - The editor instance containing the schema and configuration
 * @param tr - The transaction to modify with the migration operations
 * @returns The modified transaction with block math string replacements
 */
export function createBlockMathMigrateTransaction(editor: Editor, tr: Transaction) {
    const { schema } = editor;
    const { blockMath, paragraph } = schema.nodes;
    const zydefnBlock = schema.nodes.zydefnBlock;
    const zydefn = schema.nodes.zydefn;

    tr.doc.descendants((node, pos) => {
        if (!node.isText || !node.text) {
            return;
        }
        const { text } = node;
        if (!text.match(zybooksBlockLatexRegex)) {
            return;
        }

        const match = text.split(zybooksBlockLatexRegex).filter(m => m?.trim().length > 0);
        if (!match || match.length === 0) {
            return;
        }

        const mappedPos = tr.mapping.map(pos);
        const $from = tr.doc.resolve(mappedPos);
        const parent = $from.parent;
        const grandparent = $from.node(-1);
        const parentIsParagraph = parent.type === paragraph;
        const parentIsZydefn = parent.type === zydefn;
        const grandparentIsZydefnBlock = grandparent?.type?.name === 'zydefnBlock';

        if (parentIsZydefn && zydefnBlock) {
            const paraDepth = $from.depth - 1;
            const paragraphStart = $from.before(paraDepth);
            const paragraphEnd = $from.after(paraDepth);
            const paragraphNode = $from.node(paraDepth);
            const offsetToZydefnStart = $from.before() - paragraphStart;
            const offsetToZydefnEnd = $from.after() - paragraphStart;
            const parentOffset = $from.parentOffset;
            const contentAfterTextNode = parent.content.cut(parentOffset + node.nodeSize, parent.content.size);

            const replacement: ReturnType<typeof schema.node>[] = [];

            const contentBeforeZydefn = fragmentWithoutEmptyZydefn(paragraphNode.content.cut(0, offsetToZydefnStart));
            if (contentBeforeZydefn.size > 0) {
                replacement.push(paragraph.create(null, contentBeforeZydefn));
            }

            const innerBlocks: ReturnType<typeof schema.node>[] = [];
            const contentBeforeTextNode = parent.content.cut(0, parentOffset);
            if (match.length === 1) {
                if (contentBeforeTextNode.size > 0) {
                    innerBlocks.push(paragraph.create(null, contentBeforeTextNode));
                }
                innerBlocks.push(blockMath.create({ latex: match[0].slice(2, -2) }));
                if (contentAfterTextNode.size > 0) {
                    innerBlocks.push(paragraph.create(null, contentAfterTextNode));
                }
            } else {
                let pendingTextParts: ReturnType<typeof schema.text>[] = [];
                let contentBeforeTextNodeUsed = false;
                for (let i = 0; i < match.length; i++) {
                    const m = match[i];
                    if (isBlockMath(m)) {
                        if (pendingTextParts.length > 0) {
                            let paraContent = Fragment.fromArray(pendingTextParts);
                            if (!contentBeforeTextNodeUsed && contentBeforeTextNode.size > 0) {
                                paraContent = contentBeforeTextNode.append(paraContent);
                                contentBeforeTextNodeUsed = true;
                            }
                            innerBlocks.push(paragraph.create(null, paraContent));
                            pendingTextParts = [];
                        }
                        innerBlocks.push(blockMath.create({ latex: m.slice(2, -2) }));
                    } else {
                        pendingTextParts.push(schema.text(m));
                    }
                }
                if (pendingTextParts.length > 0) {
                    let paraContent = Fragment.fromArray(pendingTextParts).append(contentAfterTextNode);
                    if (!contentBeforeTextNodeUsed && contentBeforeTextNode.size > 0) {
                        paraContent = contentBeforeTextNode.append(paraContent);
                    }
                    if (paraContent.size > 0) {
                        innerBlocks.push(paragraph.create(null, paraContent));
                    }
                } else if (contentAfterTextNode.size > 0) {
                    let paraContent = contentAfterTextNode;
                    if (!contentBeforeTextNodeUsed && contentBeforeTextNode.size > 0) {
                        paraContent = contentBeforeTextNode.append(contentAfterTextNode);
                    }
                    innerBlocks.push(paragraph.create(null, paraContent));
                }
            }

            if (innerBlocks.length > 0) {
                const zydefnBlockNode = zydefnBlock.create(parent.attrs, Fragment.fromArray(innerBlocks));
                replacement.push(zydefnBlockNode);
            }

            const contentAfterZydefn = fragmentWithoutEmptyZydefn(paragraphNode.content.cut(offsetToZydefnEnd, paragraphNode.content.size));
            if (contentAfterZydefn.size > 0) {
                replacement.push(paragraph.create(null, contentAfterZydefn));
            }

            if (replacement.length > 0) {
                tr.replaceWith(paragraphStart, paragraphEnd, Fragment.fromArray(replacement));
            }
            return;
        }

        if (match.length > 1 && parentIsParagraph && grandparentIsZydefnBlock) {
            const paragraphStart = $from.before();
            const paragraphEnd = $from.after();
            const parentOffset = $from.parentOffset;
            const contentAfterTextNode = parent.content.cut(parentOffset + node.nodeSize, parent.content.size);

            const replacement: ReturnType<typeof schema.node>[] = [];

            const contentBefore = parent.content.cut(0, parentOffset);
            if (contentBefore.size > 0) {
                replacement.push(paragraph.create(null, contentBefore));
            }

            let pendingTextParts: ReturnType<typeof schema.text>[] = [];
            for (let i = 0; i < match.length; i++) {
                const m = match[i];
                if (isBlockMath(m)) {
                    if (pendingTextParts.length > 0) {
                        replacement.push(paragraph.create(null, Fragment.fromArray(pendingTextParts)));
                        pendingTextParts = [];
                    }
                    replacement.push(blockMath.create({ latex: m.slice(2, -2) }));
                } else {
                    pendingTextParts.push(schema.text(m));
                }
            }
            if (pendingTextParts.length > 0) {
                const paraContent = Fragment.fromArray(pendingTextParts).append(contentAfterTextNode);
                if (paraContent.size > 0) {
                    replacement.push(paragraph.create(null, paraContent));
                }
            }

            if (replacement.length > 0) {
                tr.replaceWith(paragraphStart, paragraphEnd, Fragment.fromArray(replacement));
            }
            return;
        }

        if (match.length > 1 && parentIsParagraph && !grandparentIsZydefnBlock) {
            const paragraphStart = $from.before();
            const paragraphEnd = $from.after();
            const parentOffset = $from.parentOffset;
            const contentAfterTextNode = parent.content.cut(parentOffset + node.nodeSize, parent.content.size);

            const replacement: ReturnType<typeof schema.node>[] = [];

            const contentBefore = parent.content.cut(0, parentOffset);
            if (contentBefore.size > 0) {
                replacement.push(paragraph.create(null, contentBefore));
            }

            let pendingTextParts: ReturnType<typeof schema.text>[] = [];
            for (let i = 0; i < match.length; i++) {
                const m = match[i];
                if (isBlockMath(m)) {
                    if (pendingTextParts.length > 0) {
                        replacement.push(paragraph.create(null, Fragment.fromArray(pendingTextParts)));
                        pendingTextParts = [];
                    }
                    replacement.push(blockMath.create({ latex: m.slice(2, -2) }));
                } else {
                    pendingTextParts.push(schema.text(m));
                }
            }
            if (pendingTextParts.length > 0) {
                const paraContent = Fragment.fromArray(pendingTextParts).append(contentAfterTextNode);
                if (paraContent.size > 0) {
                    replacement.push(paragraph.create(null, paraContent));
                }
            } else if (contentAfterTextNode.size > 0) {
                replacement.push(paragraph.create(null, contentAfterTextNode));
            }

            if (replacement.length > 0) {
                tr.replaceWith(paragraphStart, paragraphEnd, Fragment.fromArray(replacement));
            }
            return;
        }

        if (match.length > 1) {
            const newNodes = match.map(m => {
                if (isBlockMath(m)) {
                    return blockMath.create({ latex: m.slice(2, -2) });
                }
                return schema.text(m);
            });
            if (parent.canReplace($from.index(), $from.index() + 1, Fragment.fromArray(newNodes))) {
                tr.replaceWith(mappedPos, mappedPos + node.nodeSize, Fragment.fromArray(newNodes));
            }
            return;
        }

        const start = text.indexOf(match[0]);
        const from = tr.mapping.map(pos + start);
        const to = tr.mapping.map(pos + start + match[0].length);

        tr.replaceWith(from, to, blockMath.create({ latex: match[0].slice(2, -2) }));
    });

    // don't add to history
    tr.setMeta('addToHistory', false);
    return tr;
}

export type MathEditKind = 'inline' | 'block';

export type MathEditAttrs = {
    latex: string;
    mathml: string;
};

export type DecomposeMathResult = {
    editFrom: number;
    editTo: number;
    kind: MathEditKind;
};

function isMathEditHardBreak(node: Node): boolean {
    return node.type.name === 'hardBreak';
}

export function mathEditLeafText(node: Node): string {
    return isMathEditHardBreak(node) ? '\n' : '';
}

export function getMathEditTextBetween(doc: Node, from: number, to: number): string {
    return doc.textBetween(from, to, undefined, mathEditLeafText);
}

function latexToMathEditFragment(schema: Node['type']['schema'], text: string): Fragment {
    const { hardBreak } = schema.nodes;
    const parts = text.split('\n');
    const nodes: Node[] = [];

    parts.forEach((part, index) => {
        if (part) {
            nodes.push(schema.text(part));
        }
        if (index < parts.length - 1) {
            nodes.push(hardBreak.create());
        }
    });

    if (nodes.length === 0) {
        nodes.push(schema.text(''));
    }

    return Fragment.fromArray(nodes);
}

export function insertMathEditText(tr: Transaction, from: number, to: number, text: string): Transaction {
    if (!text.includes('\n')) {
        return tr.insertText(text, from, to);
    }

    const { schema } = tr.doc.type;
    return tr.replaceWith(from, to, latexToMathEditFragment(schema, text));
}

export function isMathNodeType(typeName: string): boolean {
    return typeName === 'inlineMath' || typeName === 'blockMath';
}

export function getMathEditKind(node: Node): MathEditKind | null {
    if (node.type.name === 'inlineMath') return 'inline';
    if (node.type.name === 'blockMath') return 'block';
    return null;
}

export function getMathEditText(node: Node): string {
    if (node.attrs.mathml) {
        return node.attrs.mathml as string;
    }

    if (node.type.name === 'inlineMath') {
        return `\\(${node.attrs.latex}\\)`;
    }
    if (node.type.name === 'blockMath') {
        return `\\[${node.attrs.latex}\\]`;
    }
    return '';
}

export function parseMathEditText(text: string, kind: MathEditKind): MathEditAttrs | null {
    if (text.startsWith('<math') && text.endsWith('</math>')) {
        return { latex: '', mathml: text };
    }

    if (kind === 'inline') {
        const match = text.match(/^\\\(([\s\S]*)\\\)$/);
        if (!match) return null;
        return { latex: match[1], mathml: '' };
    }

    const match = text.match(/^\\\[([\s\S]*)\\\]$/);
    if (!match) return null;
    return { latex: match[1], mathml: '' };
}

const inlineLatexPattern = /\\\((?:[^\\]|\\(?!\)))*?\\\)/g;
const blockLatexPattern = /\\\[[\s\S]*?\\\]/g;
const mathmlPattern = /<math[\s\S]*?<\/math>/g;

const inlineMathPatterns = [mathmlPattern, inlineLatexPattern];
const blockMathPatterns = [mathmlPattern, blockLatexPattern];

function buildTextNodePositionMap(parentNode: Node, parentStart: number): { text: string; posAt: (index: number) => number } {
    let text = '';
    const positions: number[] = [];

    parentNode.forEach((child, offset) => {
        const childStart = parentStart + offset;
        if (child.isText && child.text) {
            for (let i = 0; i < child.text.length; i++) {
                positions.push(childStart + i);
                text += child.text[i];
            }
            return;
        }
        if (isMathEditHardBreak(child)) {
            positions.push(childStart);
            text += '\n';
        }
    });

    return {
        text,
        posAt: (index: number) => positions[index],
    };
}

function matchRangeToDocPositions(match: RegExpExecArray, posAt: (index: number) => number): { from: number; to: number } {
    const from = posAt(match.index);
    const to = posAt(match.index + match[0].length - 1) + 1;
    return { from, to };
}

function findDelimitedRangeInParent(
    parentNode: Node,
    parentStart: number,
    anchorPos: number,
    patterns: RegExp[],
): { from: number; to: number } | null {
    const { text, posAt } = buildTextNodePositionMap(parentNode, parentStart);
    if (!text) {
        return null;
    }

    for (const pattern of patterns) {
        pattern.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = pattern.exec(text)) !== null) {
            const { from, to } = matchRangeToDocPositions(match, posAt);
            if (anchorPos >= from && anchorPos <= to) {
                return { from, to };
            }
        }
    }

    return null;
}

function findDelimitedRangeInAncestors(
    _doc: Node,
    $pos: import('@tiptap/pm/model').ResolvedPos,
    anchorPos: number,
    kind: MathEditKind,
): { from: number; to: number } | null {
    const patterns = kind === 'inline' ? inlineMathPatterns : blockMathPatterns;

    for (let depth = $pos.depth; depth > 0; depth--) {
        const parentNode = $pos.node(depth);
        if (kind === 'inline') {
            if (!parentNode.isInline && !parentNode.isTextblock) {
                continue;
            }
        } else if (!parentNode.isTextblock) {
            continue;
        }

        const parentStart = $pos.start(depth);
        const range = findDelimitedRangeInParent(parentNode, parentStart, anchorPos, patterns);
        if (range) {
            return range;
        }
    }

    return null;
}

export function findMathEditRange(doc: Node, anchorPos: number, kind: MathEditKind): { from: number; to: number } | null {
    const clampedPos = Math.min(Math.max(anchorPos, 0), doc.content.size);
    const $pos = doc.resolve(clampedPos);
    return findDelimitedRangeInAncestors(doc, $pos, clampedPos, kind);
}

export function refreshPendingEditRange(
    doc: Node,
    pendingEdit: { from: number; to: number; kind: MathEditKind },
    mapping?: { map: (pos: number, bias?: number) => number },
): { from: number; to: number; kind: MathEditKind } {
    const anchor = mapping ? mapping.map(pendingEdit.from, -1) : pendingEdit.from;
    const refreshed = findMathEditRange(doc, anchor, pendingEdit.kind);
    if (refreshed) {
        return { ...pendingEdit, ...refreshed };
    }

    if (!mapping) {
        return pendingEdit;
    }

    const from = mapping.map(pendingEdit.from, -1);
    const to = Math.max(from + 1, mapping.map(pendingEdit.to, 1));

    return {
        ...pendingEdit,
        from,
        to: Math.min(to, doc.content.size),
    };
}

export function tryComposePendingMath(
    tr: Transaction,
    pendingEdit: { from: number; to: number; kind: MathEditKind },
    anchorPos?: number,
): number | null {
    const anchor = anchorPos ?? pendingEdit.from;
    const range = findMathEditRange(tr.doc, anchor, pendingEdit.kind) ?? pendingEdit;
    const text = getMathEditTextBetween(tr.doc, range.from, range.to);
    if (!parseMathEditText(text, pendingEdit.kind)) {
        return null;
    }
    return composeMathText(tr, range.from, range.to, pendingEdit.kind);
}

function getMathEditCursorPos(from: number, editText: string, cursorAt: 'start' | 'end', isMathML: boolean): number {
    if (isMathML) {
        return cursorAt === 'start' ? from : from + editText.length;
    }
    const delimiterLen = 2;
    return cursorAt === 'start' ? from + delimiterLen : from + editText.length - delimiterLen;
}

export function decomposeMathNode(tr: Transaction, pos: number, cursorAt: 'start' | 'end'): DecomposeMathResult | null {
    const node = tr.doc.nodeAt(pos);
    if (!node) return null;

    const kind = getMathEditKind(node);
    if (!kind) return null;

    const editText = getMathEditText(node);
    const isMathML = Boolean(node.attrs.mathml);
    const { schema } = tr.doc.type;

    const editContent = latexToMathEditFragment(schema, editText);

    if (kind === 'inline') {
        tr.replaceWith(pos, pos + node.nodeSize, editContent);
        const editFrom = pos;
        const editTo = pos + editContent.size;
        tr.setSelection(TextSelection.create(tr.doc, getMathEditCursorPos(editFrom, editText, cursorAt, isMathML)));
        return { editFrom, editTo, kind };
    }

    const { paragraph } = schema.nodes;
    tr.replaceWith(pos, pos + node.nodeSize, paragraph.create({ textAlign: 'center' }, editContent));
    const editFrom = pos + 1;
    const editTo = editFrom + editContent.size;
    tr.setSelection(TextSelection.create(tr.doc, getMathEditCursorPos(editFrom, editText, cursorAt, isMathML)));
    return { editFrom, editTo, kind };
}

export function composeMathText(tr: Transaction, from: number, to: number, kind: MathEditKind): number | null {
    const text = getMathEditTextBetween(tr.doc, from, to);
    const attrs = parseMathEditText(text, kind);
    if (!attrs) return null;

    const { schema } = tr.doc.type;
    const { inlineMath, blockMath, paragraph } = schema.nodes;
    const mathml = kind === 'inline' ? setMathMLDisplayAttribute(attrs.mathml, false) : setMathMLDisplayAttribute(attrs.mathml, true);

    if (kind === 'inline') {
        tr.replaceWith(from, to, inlineMath.create({ latex: attrs.latex, mathml }));
        return from;
    }

    const $from = tr.doc.resolve(from);
    for (let depth = $from.depth; depth > 0; depth--) {
        const parentNode = $from.node(depth);
        if (parentNode.type !== paragraph) continue;

        const paragraphPos = $from.before(depth);
        const contentStart = paragraphPos + 1;
        const contentEnd = paragraphPos + parentNode.nodeSize - 1;
        const mathNode = blockMath.create({ latex: attrs.latex, mathml });

        if (from === contentStart && to === contentEnd) {
            tr.replaceWith(paragraphPos, paragraphPos + parentNode.nodeSize, mathNode);
            return paragraphPos;
        }

        const beforeText = tr.doc.textBetween(contentStart, from);
        const afterText = tr.doc.textBetween(to, contentEnd);

        if (beforeText) {
            return null;
        }

        const replacement = [mathNode];
        if (afterText) {
            replacement.push(paragraph.create(null, schema.text(afterText)));
        }
        tr.replaceWith(paragraphPos, paragraphPos + parentNode.nodeSize, Fragment.fromArray(replacement));
        return paragraphPos;
    }

    return null;
}
